<template>
  <div class="net-transform-frame" :style="rectStyle" v-if="visible">
    <div
      class="net-transform-frame__edge -top"
      aria-label="Resize top"
      role="twowaypicker"
      @pointerdown="onResizePointerDown($event, ['top'])"
    ></div>
    <div
      class="net-transform-frame__edge -right"
      aria-label="Resize right"
      role="twowaypicker"
      @pointerdown="onResizePointerDown($event, ['right'])"
    ></div>
    <div
      class="net-transform-frame__edge -bottom"
      aria-label="Resize bottom"
      role="twowaypicker"
      @pointerdown="onResizePointerDown($event, ['bottom'])"
    ></div>
    <div
      class="net-transform-frame__edge -left"
      aria-label="Resize left"
      role="twowaypicker"
      @pointerdown="onResizePointerDown($event, ['left'])"
    ></div>

    <template v-if="rotatable">
      <div
        class="net-transform-frame__rotation-area -top-left"
        aria-label="Rotate top-left"
        role="rotationpicker"
        @pointerdown="onRotationPointerDown($event)"
      ></div>
      <div
        class="net-transform-frame__rotation-area -top-right"
        aria-label="Rotate top-right"
        role="rotationpicker"
        @pointerdown="onRotationPointerDown($event)"
      ></div>
      <div
        class="net-transform-frame__rotation-area -bottom-left"
        aria-label="Rotate bottom-left"
        role="rotationpicker"
        @pointerdown="onRotationPointerDown($event)"
      ></div>
      <div
        class="net-transform-frame__rotation-area -bottom-right"
        aria-label="Rotate bottom-right"
        role="rotationpicker"
        @pointerdown="onRotationPointerDown($event)"
      ></div>
    </template>

    <template v-if="resizable">
      <div
        class="net-transform-frame__corner -top-left"
        aria-label="Resize top-left"
        role="fourwaypicker"
        @pointerdown="onResizePointerDown($event, ['top', 'left'])"
      ></div>
      <div
        class="net-transform-frame__corner -top-right"
        aria-label="Resize top-right"
        role="fourwaypicker"
        @pointerdown="
          onResizePointerDown($event, ['top', 'right'])
        "
      ></div>
      <div
        class="net-transform-frame__corner -bottom-left"
        aria-label="Resize bottom-left"
        role="fourwaypicker"
        @pointerdown="
          onResizePointerDown($event, ['bottom', 'left'])
        "
      ></div>
      <div
        class="net-transform-frame__corner -bottom-right"
        aria-label="Resize bottom-right"
        role="fourwaypicker"
        @pointerdown="
          onResizePointerDown($event, ['bottom', 'right'])
        "
      ></div>
    </template>

    <template v-if="itemCopies.length > 0">
      <div
        v-for="(copy, index) in itemCopies"
        :key="index"
        class="net-transform-frame__copy"
        :style="{
          left: `${Math.round(copy.x) - cachedDimensions.x}px`,
          top: `${Math.round(copy.y) - cachedDimensions.y}px`,
          width: `${Math.round(copy.width)}px`,
          height: `${Math.round(copy.height)}px`,
          rotate: `${copy.item.rotation || 0}deg`
        }"
      ></div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { splitUid, type Uid } from "@/helpers/utility/Id";
import { useMagicKeys, useMouse } from "@vueuse/core";
import { isInList, notNull } from "@/helpers/utility/Type";
import { getBoundingBox } from "@/helpers/math/geometry";
import { keyCanvasDimensions, keyNetCanvas } from "./netCanvasInjectionKeys";

import { useNetCoords } from "./netCoordsModifier";
import { useSceneTree } from "@editor/items/scene/sceneTree";
import { useImageStore } from "@editor/items/image/images.store";
import { useSceneNodesStore } from "@editor/items/scene/sceneNodes.store";

import type { CanvasSelection, NetCanvasProps, NetSelectableItemType } from "./NetCanvasProps";
import type { Handleable } from "@core/types/Events";
import type { SceneTreeItemSize } from "@editor/items/scene/SceneTreeItem";
import type { SceneNodeType } from "@editor/items/scene/SceneNodeData";

const props = withDefaults(
  defineProps<{
    selection: CanvasSelection;
    itemSizes: { [uid: Uid<"sceneNode" | "flow">]: SceneTreeItemSize };
    blocked?: boolean;
  }>(),
  {
    blocked: false
  }
);

const emit = defineEmits<{
  (e: "select", items: { uid: Uid<NetSelectableItemType> }[]): void;
  (e: "resize"): void;
  (e: "resize-end", items: SceneTreeItemSize[]): void;
  (e: "move"): void;
  (e: "move-end", items: SceneTreeItemSize[]): void;
  (e: "contextmenu", event: MouseEvent & { items: { uid: Uid<"relation" | "sceneNode" | "flow" | "flowBranch"> }[] }): void;
  (
    e: "copy",
    items: {
      x: number;
      y: number;
      item: {
        uid: Uid<"sceneNode">;
      };
    }[]
  ): void;
}>();

// const canvasItems = useNetCanvasItems();
const canvasDimensions = inject(keyCanvasDimensions);
const netCanvas = inject(keyNetCanvas);
const imageStore = useImageStore();
const sceneTree = useSceneTree();
const sceneNodeStore = useSceneNodesStore();

const canvasProps = computed<Partial<NetCanvasProps>>(() => netCanvas?.value?.props || {});
const { toLocal } = useNetCoords({ canvasDimensions });
const { shift } = useMagicKeys();
const mouse = useMouse();
const mousePressed = ref(false);

const resizing = ref(false);

const scale = computed(() => {
  if (!canvasDimensions) return 1;
  return canvasDimensions.value.scale;
});

const selectedItemUids = computed(() => [...props.selection.selected.values()]);

const selectedNetItemUids = computed(() => {
  const selectedNetItemUids = selectedItemUids.value.filter((uid) => {
    const { type } = splitUid(uid);
    return (
      type === "sceneNode" ||
      type == "flow"
    );
  });
  return selectedNetItemUids as Uid<"sceneNode" | "flow">[];
});

const selectedItemSizes = computed(
  (): SceneTreeItemSize[] => {
    return selectedNetItemUids.value
      .map((uid) => props.itemSizes[uid])
      .filter(notNull);
  }
);

const dimensions = computed(() => {
  if (selectedItemSizes.value.length === 0)
    return {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      left: 0,
      top: 0,
      right: 0,
      bottom: 0
    };

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  selectedItemSizes.value.forEach((item) => {
    if (item.type == "flow") {
      minX = Math.min(minX, item.x - 10);
      minY = Math.min(minY, item.y - 10);
      maxX = Math.max(maxX, item.x + 10);
      maxY = Math.max(maxY, item.y + 10);
      return;
    }
    const boundingBox = getBoundingBox(item);
    minX = Math.min(minX, boundingBox.left);
    minY = Math.min(minY, boundingBox.top);
    maxX = Math.max(maxX, boundingBox.right);
    maxY = Math.max(maxY, boundingBox.bottom);
  });

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
    left: minX,
    top: minY,
    right: maxX,
    bottom: maxY
  };
});

const cachedDimensions = ref({
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  left: 0,
  top: 0,
  right: 0,
  bottom: 0
});
watch(
  dimensions,
  (newDimensions) => {
    cachedDimensions.value = newDimensions;
  },
  { immediate: true }
);

const rectStyle = computed(() => {
  const { x, y, width, height } = cachedDimensions.value;
  return {
    left: `${Math.round(x)}px`,
    top: `${Math.round(y)}px`,
    width: `${Math.round(width)}px`,
    height: `${Math.round(height)}px`
  };
});

const visible = computed(() => {
  return selectedItemSizes.value.length > 0;
});

// ======================
// MOVING LOGIC

const moveable = computed(() => {
  if (canvasProps.value.impactSourceElement) return false;
  if (canvasProps.value.flowSourceElement) return false;
  return selectedItemSizes.value.some((item) => {
    return !item.locked;
  });
});

const itemCopies = ref<
  {
    item: SceneTreeItemSize<SceneNodeType>;
    x: number;
    y: number;
    width: number;
    height: number;
  }[]
>([]);

const MIDDLE_MOUSE_BUTTON = 1;

function onMovePointerDown(downEvent: PointerEvent & Handleable) {
  if (props.blocked) {
    return;
  }
  if (downEvent.button == MIDDLE_MOUSE_BUTTON) {
    return; // Ignore middle mouse button
  }
  if (downEvent.composedPath().some(
    (el) => el instanceof HTMLElement && el.classList.contains("net-choose-overlay")
  )) {
    return;
  }

  mousePressed.value = true;

  const startPointerPosition = toLocal({
    x: downEvent.clientX,
    y: downEvent.clientY
  });

  if (!isPointInFrame(startPointerPosition)) {
    // If the pointer is not in the frame, do not handle the event
    return;
  }

  // ignore down events on range inputs
  if (
    downEvent.target instanceof HTMLInputElement &&
    downEvent.target.type === "range"
  ) {
    return;
  }

  if (!isEventInCanvas(downEvent) && downEvent.pointerType != "auto") {
    // If the event is not in the canvas, do not handle the event
    return;
  }

  if (
    selectedItemSizes.value.length == 1 &&
    selectedItemSizes.value[0].locked
  ) {
    // If only one item is selected and it is locked, do not handle the event
    return;
  }

  if (downEvent.button == 2) {
    if (selectedItemUids.value.length < 2) return;
    
    downEvent.preventDefault();
    document.addEventListener("contextmenu", (contextMenuEvent) => {
      if (!isPointInFrame(toLocal({
        x: contextMenuEvent.clientX,
        y: contextMenuEvent.clientY
      }))) {
        console.log("not in frame");
        return;
      }
      contextMenuEvent.preventDefault();
      const event = contextMenuEvent as MouseEvent & Handleable & {
        items?: { uid: Uid<"relation" | "sceneNode" | "flow" | "flowBranch"> }[];
      };
      event.items = selectedItemSizes.value.map((item) => ({
        uid: item.uid
      }));
      emit("contextmenu", event as MouseEvent & { items: { uid: Uid<"relation" | "sceneNode" | "flow" | "flowBranch"> }[] });
    }, { once: true, capture: true });
    return;
  }

  // TODO: decide if we want to prevent default here.
  // const clickPath = downEvent.composedPath();
  // if (clickPath.length == 0) return;
  // if ((clickPath[0] as HTMLElement).classList.contains("net-canvas")) {
  //   downEvent.hasBeenHandled = true;
  // }
  downEvent.hasBeenHandled = true;
  downEvent.handledBy = "NetTransformFrame";
  // console.log("Pointer down on transform rect", downEvent.composedPath());

  const itemStartPositions = selectedItemSizes.value.reduce(
    (acc, item) => {
      acc[item.uid] = { x: item.x, y: item.y };
      return acc;
    },
    {} as Record<string, { x: number; y: number }>
  );

  // Handle the pointer down event for moving
  console.log("Pointer down on transform rect", downEvent);

  let movement = 0;

  function onPointerMove(moveEvent: PointerEvent & Handleable) {
    if (resizing.value) {
      // If resizing is in progress, do not handle the move event
      return;
    }
    if (props.blocked) {
      return;
    }
    moveEvent.hasBeenHandled = true;
    moveEvent.handledBy = "NetTransformFrame";

    const localPosition = toLocal({
      x: moveEvent.clientX,
      y: moveEvent.clientY
    });

    let dx = localPosition.x - startPointerPosition.x;
    let dy = localPosition.y - startPointerPosition.y;

    if (moveEvent.shiftKey) {
      // if shift is pressed, move in a straight line
      if (Math.abs(dx) > Math.abs(dy)) {
        dy = 0;
      } else {
        dx = 0;
      }
    }

    movement += Math.abs(dx) + Math.abs(dy);

    // if alt is pressed, create copies of the items
    if (moveEvent.altKey) {
      if (itemCopies.value.length != selectedItemSizes.value.length) {
        itemCopies.value = selectedItemSizes.value
          .filter(
            (item): item is SceneTreeItemSize<Exclude<SceneNodeType, "flow">> => item.type != "flow"
          )
          .map((item) => ({
            item: { ...item },
            x: item.x,
            y: item.y,
            width: item.width,
            height: item.height
          })).reverse();
      }
    } else {
      itemCopies.value = [];
    }

    const itemsIncludeGridLocked = selectedItemSizes.value.some((item) => snapsToGrid(item));
    const gridSize = itemsIncludeGridLocked ? 10 : 1;

    dx = roundToNearest(dx, gridSize);
    dy = roundToNearest(dy, gridSize);

    // Update the position of the selected items
    selectedItemSizes.value.forEach((item) => {
      if (item?.locked) return;

      const itemStartPosition = itemStartPositions[item.uid];
      if (!itemStartPosition) return;

      const itemCopy = itemCopies.value.find(
        (copy) => copy.item.uid === item.uid
      );

      if (itemCopies.value.length > 0) {
        item.x = itemStartPosition.x;
        item.y = itemStartPosition.y;
        if (itemCopy) {
          itemCopy.x = itemStartPosition.x + dx;
          itemCopy.y = itemStartPosition.y + dy;
        }
      } else {
        item.x = itemStartPosition.x + dx;
        item.y = itemStartPosition.y + dy;
      }
      item.left = item.x;
      item.top = item.y;
      item.right = item.x + item.width;
      item.bottom = item.y + item.height;
      item.center = {
        x: item.x + item.width / 2,
        y: item.y + item.height / 2
      };

      item.generated = false;
    });

    emit("move");
  }

  function onMovePointerUp(upEvent: PointerEvent & Handleable) {
    // Clean up event listeners
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onMovePointerUp);

    if (resizing.value) {
      // If resizing is in progress, do not handle the up event
      return;
    }

    if (props.blocked) {
      return;
    }

    if (movement < 3) {
      // If the pointer didn't move significantly, trigger any click actions
      // (don't prevent default)

      // if no items were in the click chain, deselect all items
      const path = upEvent.composedPath();
      const clickedItems = path.filter(
        (el) =>
          el instanceof HTMLElement &&
          (el.classList.contains("net-item") ||
            el instanceof HTMLInputElement ||
            el instanceof HTMLButtonElement)
      ) as HTMLElement[];
      if (clickedItems.length == 0) {
        const clickWasInCanvas = path.some(
          (el) =>
            el instanceof HTMLElement && el.classList.contains("net-canvas")
        );
        if (clickWasInCanvas && downEvent.pointerType != "auto") {
          console.log("clicked on empty space in canvas", upEvent, path);
          // We ignore auto events to make sure that automatically created pointer events
          // do not deselect the items directly.
          emit("select", []);
        }
      } else if (downEvent.pointerType != "auto") {
        console.log("downevent", downEvent);
        if (!upEvent.shiftKey) {
          const firstItemElement = path.find(
            (el) =>
              el instanceof HTMLElement && el.classList.contains("net-item")
          ) as HTMLElement | undefined;
          if (firstItemElement) {
            const uid = firstItemElement.dataset.uid as Uid<NetSelectableItemType> | undefined;
            if (uid) {
              emit("select", [{ uid }]);
            }
          }
        }
      }
    } else {
      upEvent.preventDefault();
      upEvent.hasBeenHandled = true;
      upEvent.handledBy = "NetTransformFrame";

      if (itemCopies.value.length > 0) {
        emit("copy", itemCopies.value);
      } else {
        emit("move-end", selectedItemSizes.value);
      }
    }

    itemCopies.value = [];
  }

  // Add event listeners for pointer move and up
  document.addEventListener("pointermove", onPointerMove);
  document.addEventListener("pointerup", onMovePointerUp);
}

function updateMoveCursor(event: PointerEvent) {
  if (!moveable.value) {
    document.body.style.cursor = "default";
    return;
  }

  if (!isEventInCanvas(event)) {
    document.body.style.cursor = "default";
    return;
  }

  if (event.composedPath().some(
    (el) => el instanceof HTMLElement && el.classList.contains("net-choose-overlay")
  )) {
    document.body.style.cursor = "default";
    return;
  }

  const pointerPosition = toLocal({
    x: event.clientX,
    y: event.clientY
  });
  if (isPointInFrame(pointerPosition)) {
    document.body.style.cursor = "move";
  } else {
    document.body.style.cursor = "default";
  }
}

onMounted(() => {
  // Attach the pointer down event listener to the transform rect
  document.addEventListener("pointerdown", onMovePointerDown, { capture: true });
  // Attach the pointer move event listener to update the cursor
  document.addEventListener("pointermove", updateMoveCursor, { passive: true });
  document.addEventListener("pointerup", () => {
    mousePressed.value = false;
  });
});

watch(
  () => selectedItemSizes.value.map((i) => i.uid).join(""),
  () => {
    const items = selectedItemSizes.value;
    if (items.length == 0) return;
    if (!mousePressed.value) return;

    const downPosition = toLocal({
      x: mouse.x.value,
      y: mouse.y.value
    });
    if (isPointInFrame(downPosition)) {
      const pointerDownEvent = new PointerEvent("pointerdown", {
        clientX: mouse.x.value,
        clientY: mouse.y.value,
        bubbles: true,
        cancelable: true,
        composed: true,
        pointerType: "auto"
      });
      onMovePointerDown(pointerDownEvent);
    }
  }
);

onBeforeUnmount(() => {
  // Clean up the event listener when the component is unmounted
  document.removeEventListener("pointerdown", onMovePointerDown, { capture: true });
  document.removeEventListener("pointermove", updateMoveCursor);
  document.body.style.cursor = "default";
});

function isPointInFrame(point: { x: number; y: number }) {
  const { x, y, width, height } = cachedDimensions.value;
  return (
    point.x >= x &&
    point.x <= x + width &&
    point.y >= y &&
    point.y <= y + height
  );
}

function isEventInCanvas(event: PointerEvent) {
  const path = event.composedPath();
  return path.some(
    (el) => el instanceof HTMLElement && el.classList.contains("net-canvas")
  );
}

// ======================
// MOVING WITH KEYBOARD

function onKeyDown(event: KeyboardEvent) {
  if (props.blocked || !moveable.value) {
    return;
  }
  if (
    event.target instanceof HTMLInputElement ||
    event.target instanceof HTMLTextAreaElement
  ) {
    // Ignore keydown events in input fields
    return;
  }

  let moveStep = selectedItemSizes.value.some(
    (item) => snapsToGrid(item)
  )
    ? 10
    : Math.round(10 / scale.value);

  if (shift.value) {
    moveStep *= 10; // If shift is pressed, move in larger steps
  }

  if (event.key === "ArrowUp" || event.key === "ArrowDown") {
    if (!isEventInsideInput(event)) {
      event.preventDefault();
      const dy = event.key === "ArrowUp" ? -moveStep : moveStep;
      selectedItemSizes.value.forEach((item) => {
        if (item.locked) return;
        item.y += dy;
        item.top += dy;
        item.bottom += dy;
        item.center.y += dy;

        item.generated = false;
      });
      emit("move-end", selectedItemSizes.value);
    }
  }
  if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
    if (!isEventInsideInput(event)) {
      event.preventDefault();
      const dx = event.key === "ArrowLeft" ? -moveStep : moveStep;
      selectedItemSizes.value.forEach((item) => {
        if (item.locked) return;
        item.x += dx;
        item.left += dx;
        item.right += dx;
        item.center.x += dx;
        
        item.generated = false;
      });
      emit("move-end", selectedItemSizes.value);
    }
  }
}

function isEventInsideInput(event: KeyboardEvent) {
  const path = event.composedPath();
  return path.some(
    (el) =>
      el instanceof HTMLInputElement ||
      el instanceof HTMLTextAreaElement ||
      el instanceof HTMLSelectElement ||
      (el instanceof HTMLElement && el.isContentEditable)
  );
}

// Attach the keydown event listener to the document
document.addEventListener("keydown", onKeyDown);

// Detach the keydown event listener when the component is unmounted
onBeforeUnmount(() => {
  document.removeEventListener("keydown", onKeyDown);
});

// ======================
// RESIZING LOGIC

type Edge = "left" | "right" | "top" | "bottom";

const resizable = computed(() => {
  if (canvasProps.value.impactSourceElement) return false;
  if (canvasProps.value.flowSourceElement) return false;
  return selectedItemSizes.value.some((item) => {
    if (item.locked) return false;
    return item.type !== "flow";
  })
});

function onResizePointerDown(event: PointerEvent & Handleable, edges: Edge[]) {
  if (!resizable.value) {
    return;
  }
  if (props.blocked) {
    return;
  }
  if (event.button == MIDDLE_MOUSE_BUTTON) {
    return; // Ignore middle mouse button
  }

  event.preventDefault();
  event.hasBeenHandled = true;
  event.handledBy = "NetTransformFrame";
  // Handle the pointer down event for resizing
  console.log(`Pointer down on ${edges}`, event);

  const startPointerPosition = toLocal({
    x: event.clientX,
    y: event.clientY
  });
  const startDimensions = {
    ...cachedDimensions.value
  };
  const startItemDimensions = selectedItemSizes.value.reduce(
    (acc, item) => {
      const boundingBox = getBoundingBox(item);
      const minSize = getMinSize(item);
      const minSizeBoundingBox = getBoundingBox({
        x: boundingBox.x,
        y: boundingBox.y,
        width: minSize.width,
        height: minSize.height,
        rotation: item.rotation || 0
      });
      if (edges.includes("right")) {
        minSizeBoundingBox.left = startDimensions.left;
        minSizeBoundingBox.right =
          minSizeBoundingBox.left + minSizeBoundingBox.width;
      }
      if (edges.includes("left")) {
        minSizeBoundingBox.right = startDimensions.right;
        minSizeBoundingBox.left =
          minSizeBoundingBox.right - minSizeBoundingBox.width;
      }
      if (edges.includes("top")) {
        minSizeBoundingBox.bottom = startDimensions.bottom;
        minSizeBoundingBox.top =
          minSizeBoundingBox.bottom - minSizeBoundingBox.height;
      }
      if (edges.includes("bottom")) {
        minSizeBoundingBox.top = startDimensions.top;
        minSizeBoundingBox.bottom =
          minSizeBoundingBox.top + minSizeBoundingBox.height;
      }
      acc[item.uid] = { ...boundingBox, minSize: minSizeBoundingBox };
      return acc;
    },
    {} as Record<
      string,
      {
        x: number;
        y: number;
        width: number;
        height: number;
        minSize: {
          width: number;
          height: number;
          left: number;
          top: number;
          bottom: number;
          right: number;
        };
      }
    >
  );

  const aspectRatio = startDimensions.width / startDimensions.height;
  resizing.value = true;

  const allItemsAspectRatioFixed = selectedItemSizes.value.every(
    (item) => item.aspectRatioLocked
  );

  function onResizePointerMove(moveEvent: PointerEvent & Handleable) {
    if (props.blocked) {
      return;
    }

    moveEvent.preventDefault();
    moveEvent.hasBeenHandled = true;
    moveEvent.handledBy = "NetTransformFrame";

    const localPosition = toLocal({
      x: moveEvent.clientX,
      y: moveEvent.clientY
    });

    const dx = localPosition.x - startPointerPosition.x;
    const dy = localPosition.y - startPointerPosition.y;

    // Calculate new dimensions based on the corner being resized
    const newDimensions = { ...startDimensions };

    const keepAspectRatio =
      shift.value || allItemsAspectRatioFixed;

    if (edges.includes("left")) {
      newDimensions.width -= dx;
      if (moveEvent.altKey) {
        newDimensions.width -= dx;
        newDimensions.width = Math.max(newDimensions.width, 1);
      } else {
        console.log(newDimensions.x, newDimensions.width, dx);
        newDimensions.width = Math.max(newDimensions.width, 1);
      }
    } else if (edges.includes("right")) {
      newDimensions.width += dx;
      if (moveEvent.altKey) {
        newDimensions.width += dx;
        newDimensions.width = Math.max(newDimensions.width, 1);
      } else {
        newDimensions.width = Math.max(newDimensions.width, 1);
      }
    }
    if (edges.includes("top")) {
      newDimensions.height -= dy;
      if (moveEvent.altKey) {
        newDimensions.height -= dy;
        newDimensions.height = Math.max(newDimensions.height, 1);
      } else {
        newDimensions.height = Math.max(newDimensions.height, 1);
      }
    } else if (edges.includes("bottom")) {
      newDimensions.height += dy;
      if (moveEvent.altKey) {
        newDimensions.height += dy;
        newDimensions.height = Math.max(newDimensions.height, 1);
      } else {
        newDimensions.height = Math.max(newDimensions.height, 1);
      }
    }

    // Fix the dimensions to maintain aspect ratio if needed
    if (keepAspectRatio) {
      if (edges.includes("left") || edges.includes("right")) {
        // horizontal resize
        newDimensions.height = Math.max(
          1,
          newDimensions.width / aspectRatio
        );
      } else if (edges.includes("top") || edges.includes("bottom")) {
        // vertical resize
        newDimensions.width = Math.max(1, newDimensions.height * aspectRatio);
      }
    }

    // Update positions based on new dimensions
    if (edges.includes("left")) {
      if (moveEvent.altKey) {
        newDimensions.x = startDimensions.x + (startDimensions.width - newDimensions.width) / 2;
      } else {
        newDimensions.x = startDimensions.x + startDimensions.width - newDimensions.width;
      }
    } else if (edges.includes("right")) {
      if (moveEvent.altKey) {
        newDimensions.x = startDimensions.x + (startDimensions.width - newDimensions.width) / 2;
      }
    } else {
      if (keepAspectRatio) {
        // adjust x position to keep center
        newDimensions.x =
          startDimensions.x +
          (startDimensions.width - newDimensions.width) / 2;
      }
    }

    if (edges.includes("top")) {
      if (moveEvent.altKey) {
        newDimensions.y = startDimensions.y + (startDimensions.height - newDimensions.height) / 2;
      } else {
        newDimensions.y = startDimensions.y + startDimensions.height - newDimensions.height;
      }
    } else if (edges.includes("bottom")) {
      if (moveEvent.altKey) {
        newDimensions.y = startDimensions.y + (startDimensions.height - newDimensions.height) / 2;
      }
    } else {
      if (keepAspectRatio) {
        // adjust y position to keep center
        newDimensions.y =
          startDimensions.y +
          (startDimensions.height - newDimensions.height) / 2;
      }
    }


    // Update left, top, right, bottom
    newDimensions.left = newDimensions.x;
    newDimensions.top = newDimensions.y;
    newDimensions.right = newDimensions.x + newDimensions.width;
    newDimensions.bottom = newDimensions.y + newDimensions.height;

    const { width, height } = newDimensions;

    const xDelta = newDimensions.x - startDimensions.x;
    const yDelta = newDimensions.y - startDimensions.y;

    const xScale = width / startDimensions.width;
    const yScale = height / startDimensions.height;

    // Update the dimensions of the selected items
    selectedItemSizes.value.forEach((item) => {
      if (item.locked) {
        return;
      }

      const itemStart = startItemDimensions[item.uid];
      const minSize = itemStart.minSize;
      const startX = itemStart.x;
      const startY = itemStart.y;
      const startWidth = itemStart.width;
      const startHeight = itemStart.height;
      const startBottom = startY + startHeight;
      const itemAspectRatio = startWidth / startHeight;

      let newWidth = startWidth * xScale;
      let newHeight = startHeight * yScale;

      const offsetLeftStart = startX - startDimensions.x;
      const offsetRightStart = startDimensions.right - (startX + startWidth);
      const offsetTopStart = startY - startDimensions.y;
      const offsetBottomStart = startDimensions.bottom - (startY + startHeight);
      const offsetLeftScaled = offsetLeftStart * xScale;
      const offsetTopScaled = offsetTopStart * yScale;
      const offsetRightScaled = offsetRightStart * xScale;
      const offsetBottomScaled = offsetBottomStart * yScale;
      // This works for unrotated items:
      let newX = startX + xDelta + (offsetLeftScaled - offsetLeftStart);
      let newY = startY + yDelta + (offsetTopScaled - offsetTopStart);

      const gridSize = snapsToGrid(item) ? 10 : 1;

      if (shift.value || item.aspectRatioLocked) {
        // if shift is pressed, keep the aspect ratio
        // Keep in mind that multiple edges (vertical & horizontal) can be resized at the same time
        if (edges.includes("left") || edges.includes("right")) {
          // horizontal resize
          newHeight = roundToNearest(newWidth / itemAspectRatio, gridSize);

          if (newHeight < minSize.height) {
            newHeight = minSize.height;
          }

          if (edges.includes("top")) {
            // if top edge is resized, adjust y position
            newY = roundToNearest(startBottom - newHeight, gridSize);
          }
        } else if (edges.includes("top") || edges.includes("bottom")) {
          // vertical resize
          newWidth = roundToNearest(newHeight * itemAspectRatio, gridSize);

          if (newWidth < minSize.width) {
            newWidth = minSize.width;
          }
        }
      }

      newWidth = roundToNearest(newWidth, gridSize);
      newHeight = roundToNearest(newHeight, gridSize);
      newWidth = Math.max(newWidth, minSize.width);
      newHeight = Math.max(newHeight, minSize.height);

      if (edges.includes("top")) {
        const minY = newDimensions.y + offsetTopScaled;
        const maxY = newDimensions.bottom - offsetBottomScaled;
        if (newY < minY) {
          newY = minY;
        }
        if (newY + newHeight > maxY) {
          newY = maxY - newHeight;
        }
      }

      if (edges.includes("left")) {
        const minX = newDimensions.x + offsetLeftScaled;
        const maxX = newDimensions.right - offsetRightScaled;
        if (newX < minX) {
          newX = minX;
        }
        if (newX + newWidth > maxX) {
          newX = maxX - newWidth;
        }
      }

      newX = roundToNearest(newX, gridSize);
      newY = roundToNearest(newY, gridSize);

      const itemDimensions = getRectFromRotationBbox(
        {
          x: newX,
          y: newY,
          width: newWidth,
          height: newHeight
        },
        item.rotation || 0
      );

      const roundedX = roundToNearest(itemDimensions.x, gridSize);
      const roundedY = roundToNearest(itemDimensions.y, gridSize);
      const roundedWidth = roundToNearest(itemDimensions.width, gridSize);
      const roundedHeight = roundToNearest(itemDimensions.height, gridSize);

      const newMinHeight = getElementMinHeightBasedOnWidth(roundedWidth, item);

      const finalHeight = Math.max(roundedHeight, newMinHeight);

      const newSize = {
        x: roundedX,
        y: roundedY,
        width: roundedWidth,
        height: finalHeight,
        left: roundedX,
        top: roundedY,
        right: roundedX + roundedWidth,
        bottom: roundedY + finalHeight,
        center: {
          x: roundedX + roundedWidth / 2,
          y: roundedY + finalHeight / 2
        }
      };

      Object.assign(item, newSize);
    });

    emit("resize");
  }

  function onResizePointerUp(upEvent: PointerEvent & Handleable) {
    resizing.value = false;
    document.removeEventListener("pointermove", onResizePointerMove);
    document.removeEventListener("pointerup", onResizePointerUp);

    if (props.blocked) {
      return;
    }

    upEvent.preventDefault();
    upEvent.hasBeenHandled = true;
    upEvent.handledBy = "NetTransformFrame";

    emit("resize-end", selectedItemSizes.value);
  }

  // Add event listeners for pointer move and up
  document.addEventListener("pointermove", onResizePointerMove);
  document.addEventListener("pointerup", onResizePointerUp);
}

// ======================
// == Rotation Logic ==

const rotatable = computed(() => {
  if (canvasProps.value.impactSourceElement) return false;
  if (canvasProps.value.flowSourceElement) return false;
  return (
    selectedItemSizes.value.length > 1 ||
    selectedItemSizes.value.some(
      (node) => {
        return nodeRotates(node) && !node.locked;
      }
    )
  );
});

function nodeRotates(node: SceneTreeItemSize) {
  if (node.type === "flow") return false;
  if (node.type === "element") return false;
  return true;
}

function onRotationPointerDown(event: PointerEvent & Handleable) {
  if (!rotatable.value) {
    return;
  }
  if (props.blocked) {
    return;
  }
  if (event.button == MIDDLE_MOUSE_BUTTON) {
    return;
  }

  event.preventDefault();
  event.hasBeenHandled = true;
  event.handledBy = "NetTransformFrame";

  const rotationCenter = {
    x: dimensions.value.x + dimensions.value.width / 2,
    y: dimensions.value.y + dimensions.value.height / 2
  };

  const startPointerPosition = toLocal({
    x: event.clientX,
    y: event.clientY
  });

  const startAngle = Math.atan2(
    startPointerPosition.y - rotationCenter.y,
    startPointerPosition.x - rotationCenter.x
  );

  const startItemDimensions = selectedItemSizes.value.reduce(
    (acc, item) => {
      acc[item.uid] = { ...item };
      return acc;
    },
    {} as Record<
      string,
      { x: number; y: number; width: number; height: number; rotation?: number }
    >
  );

  function onPointerMove(moveEvent: PointerEvent & Handleable) {
    if (props.blocked) {
      return;
    }

    moveEvent.preventDefault();
    moveEvent.hasBeenHandled = true;
    moveEvent.handledBy = "NetTransformFrame";

    const localPosition = toLocal({
      x: moveEvent.clientX,
      y: moveEvent.clientY
    });

    const currentAngle = Math.atan2(
      localPosition.y - rotationCenter.y,
      localPosition.x - rotationCenter.x
    );
    let angleDelta = currentAngle - startAngle;

    if (shift.value && selectedItemSizes.value.length > 1) {
      // Snap to 45 degree increments if shift is held
      angleDelta = Math.round(angleDelta / (Math.PI / 4)) * (Math.PI / 4);
    }

    // Update the rotation of the selected items
    selectedItemSizes.value.forEach((node) => {
      if (node.locked) {
        return;
      }

      // move the item relative to the rotation center
      const startItemCenter = {
        x:
          startItemDimensions[node.uid].x +
          startItemDimensions[node.uid].width / 2,
        y:
          startItemDimensions[node.uid].y +
          startItemDimensions[node.uid].height / 2
      };
      const relativeX = startItemCenter.x - rotationCenter.x;
      const relativeY = startItemCenter.y - rotationCenter.y;
      const newX =
        rotationCenter.x +
        relativeX * Math.cos(angleDelta) -
        relativeY * Math.sin(angleDelta);
      const newY =
        rotationCenter.y +
        relativeX * Math.sin(angleDelta) +
        relativeY * Math.cos(angleDelta);

      const gridSize = snapsToGrid(node) ? 10 : 1;
      
      node.x = roundToNearest(newX - node.width / 2, gridSize);
      node.y = roundToNearest(newY - node.height / 2, gridSize);

      if (nodeRotates(node)) {
        const startRotation = startItemDimensions[node.uid].rotation || 0;
        if (selectedItemSizes.value.length == 1 && shift.value) {
          // If only one item is selected and shift is held, snap to 45 degree increments
          node.rotation = roundToNearest(
            startRotation + toDegrees(angleDelta),
            45
          );
        } else {
          // Otherwise, apply the angle delta directly
          node.rotation = startRotation + toDegrees(angleDelta);
        }
      }

      // Update element dimensions
      node.left = node.x;
      node.top = node.y;
      node.right = node.x + node.width;
      node.bottom = node.y + node.height;
      node.center = {
        x: node.x + node.width / 2,
        y: node.y + node.height / 2
      };
      node.generated = false;
    });

    emit("move");
  }

  function onPointerUp(upEvent: PointerEvent & Handleable) {
    if (props.blocked) {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      return;
    }

    upEvent.preventDefault();
    upEvent.hasBeenHandled = true;
    upEvent.handledBy = "NetTransformFrame";

    emit("move-end", selectedItemSizes.value);

    // Clean up event listeners
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerUp);
  }

  // Add event listeners for pointer move and up
  document.addEventListener("pointermove", onPointerMove);
  document.addEventListener("pointerup", onPointerUp);
}

/**
 * From a bounding box of a rotated rectangle, calculate the rectangle that
 * was rotated to create the bounding box.
 */
function getRectFromRotationBbox(
  bbox: {
    x: number;
    y: number;
    width: number;
    height: number;
  },
  rotation: number
) {
  // The center of the bounding box
  const centerX = bbox.x + bbox.width / 2;
  const centerY = bbox.y + bbox.height / 2;

  // The absolute values of cos and sin of the rotation angle
  const absCos = Math.abs(Math.cos(toRadians(rotation)));
  const absSin = Math.abs(Math.sin(toRadians(rotation)));

  const maxHeight = bbox.height * absCos + bbox.width * absSin;
  const maxWidth = bbox.width * absCos + bbox.height * absSin;

  // Calculate the original width and height before rotation
  // These formulas invert the bounding box expansion caused by rotation
  let width =
    (bbox.width * absCos - bbox.height * absSin) /
      (absCos * absCos - absSin * absSin) ||
    (bbox.width * absSin - bbox.height * absCos) /
      (absSin * absSin - absCos * absCos);

  let height =
    (bbox.height * absCos - bbox.width * absSin) /
      (absCos * absCos - absSin * absSin) ||
    (bbox.height * absSin - bbox.width * absCos) /
      (absSin * absSin - absCos * absCos);

  if (height < 10) {
    height = 10;
    width = Math.min(width, maxWidth);
  }
  if (width < 10) {
    width = 10;
    height = Math.min(height, maxHeight);
  }

  if (!isNaN(width) && !isNaN(height) && width >= 10 && height >= 10) {
    // Keep.
  } else {
    // Try fitting a smaller rectangle in the bounding box.
    if (absCos > absSin) {
      width = bbox.width;
      height = (bbox.height / absCos) * absSin;
    } else {
      height = bbox.height;
      width = (bbox.width / absSin) * absCos;
    }
  }

  return {
    x: centerX - width / 2,
    y: centerY - height / 2,
    width: width,
    height: height
  };
}

function toDegrees(radians: number): number {
  return (radians * 180) / Math.PI;
}

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

function roundToNearest(value: number, step: number) {
  return Math.round(value / step) * step;
}

const sceneFontSize = computed(() => {
  return sceneTree.activeScene.fontSize;
});


const GRID_SIZE = 10;
function getMinSize(item: SceneTreeItemSize): { width: number; height: number } {
  let width = GRID_SIZE;
  let height = GRID_SIZE;

  if (isElementSize(item)) {
    width = 200;

    height = getElementMinHeightBasedOnWidth(width, item);
  } else if (isInList(item.type, ["medium", "text", "shape", "chart"])) {
    width = GRID_SIZE;
    height = GRID_SIZE;
  }

  return { width, height };
}

function getElementMinHeightBasedOnWidth(width: number, item: SceneTreeItemSize): number {
  if (!isElementSize(item)) {
    return GRID_SIZE;
  }
  let height = 0;

  const data = item.data;
  const node = sceneNodeStore.byUid[item.uid];

  const footerHeight = sceneFontSize.value + 16;

  // HEADLINE
  if (!data.headlineHidden) {
    const titleFontSize = node.fontSizeRelative * sceneFontSize.value;
    const titleHeight = Math.ceil((titleFontSize * 1.15 + titleFontSize * 0.25) / GRID_SIZE) * GRID_SIZE;
    height += titleHeight;
    height += footerHeight;
  } else {
    height += Math.ceil(footerHeight / GRID_SIZE) * GRID_SIZE;
  }

  // IMAGE
  if (data.imageId != null) {
    const image = imageStore.items[data.imageId];
    if (image) {
      const aspectRatio = image.aspectRatio ?? image.width / image.height;
      height += width / aspectRatio;
    }
  }

  height = Math.ceil(height / GRID_SIZE) * GRID_SIZE; // Round up to nearest GRID_SIZE

  return height;
}

function isElementSize(item: SceneTreeItemSize): item is SceneTreeItemSize<"element"> {
  return isInList(item.type, ["element"]);
}

function snapsToGrid(item: SceneTreeItemSize) {
  if ("gridLocked" in item && item.gridLocked) return true;
  return isInList(item.type, ["flow", "element"]);
}
</script>

<style lang="scss">
.net-transform-frame {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;

  --scale: v-bind("scale");

  &__edge {
    position: absolute;
    pointer-events: auto;
    background-color: transparent;
    display: flex;
    align-items: center;
    justify-content: center;

    --size: calc(2px / var(--scale));

    &::before {
      content: "";
      position: relative;
      width: 100%;
      height: 100%;
      background-color: var(--color-action);
      opacity: 0.5;
    }

    &.-top {
      top: -6px;
      left: 0;
      width: 100%;
      height: 10px;
      cursor: ns-resize;

      &::before {
        height: var(--size);
      }
    }
    &.-right {
      right: -6px;
      top: 0;
      width: 10px;
      height: 100%;
      cursor: ew-resize;

      &::before {
        width: var(--size);
      }
    }
    &.-bottom {
      bottom: -6px;
      left: 0;
      width: 100%;
      height: 10px;
      cursor: ns-resize;

      &::before {
        height: var(--size);
      }
    }
    &.-left {
      left: -6px;
      top: 0;
      width: 10px;
      height: 100%;
      cursor: ew-resize;

      &::before {
        width: var(--size);
      }
    }
  }

  &__corner {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: auto;

    --size: calc(20px / var(--scale));
    --half-size: calc(var(--size) / 2);
    --neg-half-size: calc(var(--half-size) * -1);
    width: var(--size);
    height: var(--size);

    &::before {
      content: "";
      --border-width: calc(2px / var(--scale));
      width: var(--half-size);
      height: var(--half-size);
      border: var(--border-width) solid var(--color-action);
      background-color: var(--color-background-close);
      border-radius: var(--border-width);
    }

    &.-top-left {
      top: var(--neg-half-size);
      left: var(--neg-half-size);
      cursor: nwse-resize;
    }
    &.-top-right {
      top: var(--neg-half-size);
      right: var(--neg-half-size);
      cursor: nesw-resize;
    }
    &.-bottom-left {
      bottom: var(--neg-half-size);
      left: var(--neg-half-size);
      cursor: nesw-resize;
    }
    &.-bottom-right {
      bottom: var(--neg-half-size);
      right: var(--neg-half-size);
      cursor: nwse-resize;
    }
  }

  &__rotation-area {
    position: absolute;
    pointer-events: auto;

    --size: calc(30px / var(--scale));
    width: var(--size);
    height: var(--size);

    --offset-size: calc(var(--size) * -1 + 4px);

    &.-top-left {
      top: var(--offset-size);
      left: var(--offset-size);
      rotate: -45deg;
      cursor: nesw-resize;
    }
    &.-top-right {
      top: var(--offset-size);
      right: var(--offset-size);
      rotate: 45deg;
      cursor: nwse-resize;
    }
    &.-bottom-left {
      bottom: var(--offset-size);
      left: var(--offset-size);
      rotate: 225deg;
      cursor: nwse-resize;
    }
    &.-bottom-right {
      bottom: var(--offset-size);
      right: var(--offset-size);
      rotate: -225deg;
      cursor: nesw-resize;
    }
  }

  &__copy {
    position: absolute;
    pointer-events: none;
    background-color: rgba(0, 0, 0, 0.1);
    border: 1px dashed var(--color-action);
    box-sizing: border-box;
    z-index: 1; // Ensure copies are behind the main selection rect
  }
}
</style>
